import { Stack, Typography } from '@mui/material';
import { Frame } from '../../components/layouts/Frame';
import { DataTable } from '../../components/global/Table/DataTable';
import { Link } from '../../components/global/Link';
import { DataTableHeadCellData } from '../../components/global/Table/DataTableHeader';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAPI } from '../../hooks/useAPI';
import { InvoiceData } from '../../types';
import { DownloadIcon } from '../../assets/svgs/DownloadIcon';
import moment from 'moment';

const headCells: DataTableHeadCellData[] = [
  {
    id: 'invoice_number',
    disablePadding: true,
    label: 'Invoice Number',
    align: 'left',
    sortable: true,
    searchable: true,
    width: '200px',
  },
  {
    id: 'date',
    disablePadding: true,
    label: 'Date',
    align: 'left',
    cellAlign: 'left',
    sortable: true,
    searchable: true,
    width: '100px',
  },
  {
    id: 'empty',
    disablePadding: false,
    label: '',
    align: 'left',
    cellAlign: 'left',
  },
  {
    id: 'download',
    disablePadding: false,
    label: 'Download',
    width: '50px',
  },
];

interface InvoiceTableData {
  id: any;
  invoice_number: any;
  date: any;
  empty: any;
  download: JSX.Element;
}

export const Invoices = () => {
  const isLoading = useRef(false);
  const [sIsLoading, setSIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { fetchData, token } = useAPI();
  const [rows, setRows] = useState<InvoiceTableData[]>([]);
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);

  const onDownload = async (id: any) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/invoices/download/${id}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/pdf',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to download invoice');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const refetchInvoices = async () => {
    setSIsLoading(true);
    isLoading.current = true;
    const { errors, data } = await fetchData('/invoices');
    setInvoices(data ?? []);
    isLoading.current = false;
    setSIsLoading(false);
  };

  const getRows = (invoices: InvoiceData[]) => {
    const result: InvoiceTableData[] = [];
    for (let i = 0; i < invoices.length; i++) {
      result.push({
        id: `${invoices[i].id}`,
        invoice_number: <Typography>{invoices[i].invoice_number}</Typography>,
        date: (
          <Typography>
            {moment(invoices[i].created_at).format('MM/DD/YYYY')}
          </Typography>
        ),
        empty: '',
        download: (
          <Link to={`#`} onClick={() => onDownload(invoices[i].id)}>
            <DownloadIcon />
          </Link>
        ),
      });
    }
    return result;
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading.current) {
      refetchInvoices();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setRows(getRows(invoices));
  }, [invoices]);

  return (
    <Frame title="Invoices">
      <Stack>
        <DataTable
          title="Invoices"
          headCells={headCells}
          rows={rows}
          isLoading={sIsLoading}
          hideIdCheckbox={true}
          hideDeleteButton={true}
        />
      </Stack>
    </Frame>
  );
};
